import type { AppBoleta, AppBoletaProducto, AppBoletaCampo } from '../../types/empacadora';

// El Grupo de Forza solo aplica a los estadios Post Forza (FPC/FRC). La boleta
// no guarda el código de estadio sino el nombre de la etapa, que es el mismo
// texto del catálogo ("Post Forza PC" / "Post Forza RC").
function esPostForza(etapaNombre?: string | null): boolean {
  return (etapaNombre ?? '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .trim().toLowerCase()
    .startsWith('post forza');
}

function fmtFLong(s?: string | null) {
  if (!s) return '';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-CR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

// La fecha_plan se calculó como fecha ancla de la etapa + días del tratamiento.
// Ambos valores quedan guardados en la boleta, por lo que esta fecha es histórica
// y no cambia aunque después se modifique el bloque o el plan de manejo.
function fechaActualizacionEtapa(b: AppBoleta): string | null {
  if (!b.fecha_plan || b.dias == null) return null;
  const d = new Date(b.fecha_plan + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() - Number(b.dias));
  return d.toISOString().slice(0, 10);
}

function fmtNPrint(n?: number | null, dec = 2) {
  if (n == null) return '';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// Checkbox marcado o vacío para impresión
function chk(activo?: boolean | null) {
  return activo ? '<span class="check-box check-on">☒</span>' : '<span class="check-box">☐</span>';
}

// Hora corta dd/mm hh:mm
function fmtDateTimeShort(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Solo fecha DD/MM/YYYY a partir de ISO con hora
function fmtFechaCorta(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Solo hora HH:MM a partir de ISO con hora
function fmtHoraCorta(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function buildBoletaHTML(
  b: AppBoleta,
  prods: AppBoletaProducto[],
  empresaNombre: string,
  empresaCedula: string,
  ubicacion: string,
  empresaLogo = '',
  campo?: AppBoletaCampo | null,
): string {
  // Excluir productos marcados como excluidos por aprobación del ingeniero — no se imprimen
  const productosRows = prods.filter(p => !p.es_agua && !p.excluida).map((p, i) => `
    <tr>
      <td class="num">${i + 1}</td>
      <td class="left bold">${p.nombre_producto}</td>
      <td class="left small gray">${p.ingrediente_activo || p.descripcion || ''}</td>
      <td class="left">${p.justificacion ?? ''}</td>
      <td class="right">${fmtNPrint(p.dosis, 2)}</td>
      <td class="right">${fmtNPrint(p.cantidad_boom, 2)}</td>
      <td class="right">${fmtNPrint(p.cantidad_fraccion, 2)}</td>
      <td class="right bold">${fmtNPrint(p.cantidad_aplicar, 2)}</td>
      <td class="center">${p.medida ?? ''}</td>
      <td class="center">${p.latencia ?? ''}</td>
      <td class="center">${p.reingreso ?? 'N/A'}</td>
    </tr>`).join('');

  const aguaRow = prods.filter(p => p.es_agua).map(p => `
    <tr class="agua-row">
      <td></td>
      <td class="left bold" colspan="3">AGUA REQUERIDA</td>
      <td class="right">${fmtNPrint(p.dosis, 2)}</td>
      <td class="right">${fmtNPrint(p.cantidad_boom, 2)}</td>
      <td class="right">${fmtNPrint(p.cantidad_fraccion, 2)}</td>
      <td class="right bold">${fmtNPrint(p.cantidad_aplicar, 2)}</td>
      <td class="center">Lts</td>
      <td></td><td></td>
    </tr>`).join('');

  const ultimaLinea = `
    <tr class="ultima">
      <td colspan="11" class="center bold">*** Ú l t i m a &nbsp; L í n e a ****</td>
    </tr>`;

  const boletaNum = b.numero?.padStart(10, '0') ?? '';
  const boomLabel = b.boom_capacidad_l ? `${b.boom_capacidad_l.toLocaleString('es-CR')} Lts` : '';
  const boomNom   = b.boom_nombre
    ? `${b.boom_nombre}${boomLabel ? ' — ' + boomLabel : ''}`
    : boomLabel || '—';
  const nBooms    = b.n_booms_completos ?? 0;
  const fraccion  = b.litros_fraccion != null ? fmtNPrint(b.litros_fraccion, 0) : '0';

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<title>Boleta ${boletaNum}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 9pt; color: #000; background: #fff; }
  @page { size: Letter landscape; margin: 8mm 10mm; }

  .page { width: 100%; max-width: 100%; padding: 2mm 3mm; overflow: hidden; }

  .hdr { display: flex; align-items: flex-start; border-bottom: 2px solid #000; padding-bottom: 4px; margin-bottom: 4px; }
  .hdr-logo { width: 70px; margin-right: 8px; }
  .hdr-logo img { width: 65px; }
  .hdr-center { flex: 1; text-align: center; }
  .hdr-center .empresa { font-size: 13pt; font-weight: bold; color: #003399; }
  .hdr-center .cedula  { font-size: 8pt; color: #333; }
  .hdr-center .bpa     { font-size: 8pt; font-style: italic; color: #003399; font-weight: bold; }
  .hdr-center .titulo-doc { font-size: 8pt; margin-top: 2px; }
  .hdr-center .emision { font-size: 7pt; color: #555; margin-top: 1px; }
  .hdr-right { width: 130px; border: 1.5px solid #000; text-align: center; padding: 3px; }
  .hdr-right .codigo { font-size: 12pt; font-weight: bold; color: #003399; }
  .hdr-right .cultivo { font-size: 7.5pt; }
  .hdr-right .variedad { font-size: 7.5pt; }

  .semana-title { text-align: center; font-size: 11pt; font-weight: bold; border-bottom: 1px solid #000; padding: 3px 0; margin-bottom: 4px; }
  .boleta-num { font-size: 14pt; font-weight: bold; color: #cc0000; }

  .info-grid { display: grid; grid-template-columns: 1fr 220px; gap: 4px; margin-bottom: 3px; }
  .info-block { display: flex; flex-direction: column; gap: 2px; }
  .info-row { display: flex; gap: 6px; font-size: 8.5pt; align-items: baseline; }
  .info-label { font-weight: bold; white-space: nowrap; }
  .info-right { border: 1px solid #999; padding: 4px 6px; font-size: 8.5pt; }
  .info-right table { width: 100%; }
  .info-right td { padding: 1px 3px; }

  .fecha-banda { background: #666; color: #fff; font-size: 9pt; font-weight: bold; padding: 2px 6px; margin-bottom: 4px; display: flex; gap: 20px; align-items: center; }
  .fecha-recom { font-size: 8.5pt; border: 1px solid #333; padding: 2px 6px; margin-bottom: 4px; display: flex; gap: 12px; align-items: baseline; }
  .fecha-recom .bold-u { font-weight: bold; text-decoration: underline; }
  .field-blank { border-bottom: 1px solid #000; display: inline-block; min-width: 80px; margin: 0 4px; }

  .tabla-wrap { overflow: hidden; }
  table.productos { width: 100%; border-collapse: collapse; font-size: 8pt; }
  table.productos th { background: #dde; border: 1px solid #999; padding: 2px 3px; text-align: center; font-size: 7.5pt; vertical-align: bottom; }
  table.productos td { border: 1px solid #bbb; padding: 2px 3px; vertical-align: top; }
  table.productos tr.agua-row td { background: #e8f0ff; font-style: italic; }
  table.productos tr.ultima td { background: #f8f8f8; font-size: 8pt; padding: 3px; border-top: 2px solid #000; }
  td.num    { text-align: center; width: 18px; }
  td.left   { text-align: left; }
  td.right  { text-align: right; width: 62px; }
  td.center { text-align: center; }
  td.bold   { font-weight: bold; }
  td.small  { font-size: 7.5pt; }
  td.gray   { color: #555; }

  .footer { margin-top: 6px; }
  .footer-top { display: flex; gap: 6px; margin-bottom: 4px; font-size: 8pt; }
  .footer-top .bloque { border: 1px solid #999; padding: 3px 6px; }
  .footer-top .bloque-label { font-weight: bold; font-size: 7pt; margin-bottom: 2px; }
  .footer-checks { display: flex; gap: 4px; align-items: center; }
  .check-box { display: inline-block; font-size: 11pt; line-height: 1; margin-right: 3px; vertical-align: middle; font-family: Arial, sans-serif; }
  .check-box.check-on { font-weight: bold; }
  .footer-firmas { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; margin-top: 30px; }
  .firma-bloque { border-top: 1px solid #000; padding-top: 2px; font-size: 8pt; }
  .footer-bottom { display: flex; justify-content: space-between; font-size: 7pt; color: #555; border-top: 1px solid #ccc; padding-top: 3px; margin-top: 4px; }
  .impresa-el { font-size: 7.5pt; color: #555; }

  .calib-table { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 4px; }
  .calib-table td, .calib-table th { border: 1px solid #bbb; padding: 2px 4px; text-align: center; }

  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
<div class="page">

  <div class="hdr">
    <div class="hdr-logo">${empresaLogo ? `<img src="${empresaLogo}" alt="Logo" style="width:65px;height:65px;object-fit:contain;">` : ''}</div>
    <div class="hdr-center">
      <div class="empresa">${empresaNombre}</div>
      <div class="cedula">Cédula Jurídica ${empresaCedula}</div>
      <div class="bpa">Manual de Buenas Prácticas Agrícolas</div>
      <div class="titulo-doc"><strong>Recomendación y Registro de Aplicación de Agroquímicos</strong></div>
      <div class="emision">Emisión: 12/12/2008 &nbsp;&nbsp; Revisión: 23/05/2026 &nbsp;&nbsp; Versión: ERP-01.0.0</div>
    </div>
    <div class="hdr-right">
      <div class="codigo">RB03-AVZ</div>
      <div class="cultivo">Cultivo: Piña</div>
      <div class="cultivo">(Ananas Comosus)</div>
      <div class="variedad">Variedad: Amarilla</div>
    </div>
  </div>

  <div class="semana-title" style="display:flex;justify-content:space-between;align-items:center;padding:3px 8px;">
    <span>Aplicación Programada Semana No. ${b.semana}</span>
    <span>
      ${b.manual ? '<span style="background:#b45309;color:#fff;font-size:7.5pt;font-weight:bold;padding:1px 7px;border-radius:3px;margin-right:6px;letter-spacing:.5px;">BOLETA MANUAL</span>' : ''}
      Boleta No. <span class="boleta-num">${boletaNum}</span>
    </span>
  </div>

  <div class="info-grid">
    <div class="info-block">
      <div class="info-row"><span class="info-label">Productor:</span><span class="info-val"><strong>${empresaNombre}</strong></span></div>
      <div class="info-row"><span class="info-label">Ubicación de la Plantación:</span><span class="info-val">${ubicacion}</span></div>
      <div class="info-row">
        <span class="info-label">Lote:</span><span class="info-val"><strong>${b.lote_num ?? ''}</strong></span>
        <span style="margin-left:12px" class="info-label">No. Plantas:</span><span class="info-val"><strong>${(b.plantas ?? 0).toLocaleString('es-CR')}</strong></span>
      </div>
      <div class="info-row">
        <span class="info-label">Fecha Siembra:</span><span class="info-val">${fmtFLong(b.fecha_siembra)}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Bloques:</span><span class="info-val">${b.bloques ?? ''}</span>
        <span style="margin-left:12px" class="info-label">Has:</span><span class="info-val"><strong>${fmtNPrint(b.area_ha, 2)}</strong></span>
      </div>
      ${esPostForza(b.etapa_nombre) ? `
      <div class="info-row">
        <span class="info-label">Grupo de Forza:</span><span class="info-val"><strong>${b.grupo_forza ?? ''}</strong></span>
      </div>` : ''}
    </div>
    <div class="info-right">
      <table>
        <tr><td class="info-label">Tratamiento:</td><td>${b.manual ? (b.tratamiento ?? '') : (b.ciclo_nombre ?? b.tratamiento ?? '')}</td></tr>
        <tr><td class="info-label">Boom:</td><td><strong>${boomNom}</strong></td></tr>
      </table>
    </div>
  </div>

  <div class="fecha-banda">
    ${b.manual ? '' : `
    <span>Etapa: <strong>${b.etapa_nombre ?? ''}</strong></span>
    <span>Fecha etapa: <strong>${fmtFLong(fechaActualizacionEtapa(b))}</strong></span>`}
    <span style="margin-left:auto">No. Boones: <strong>${nBooms}</strong></span>
    <span>Fracción: <strong>${fraccion}</strong></span>
  </div>

  <div class="fecha-recom">
    <span>Fecha recomendación: <span class="bold-u">${fmtFLong(b.fecha_plan)}</span></span>
    <span>Fecha Aplica: <span class="field-blank">${campo?.inicio_at ? `<span class="bold-u">${fmtFechaCorta(campo.inicio_at)}</span>` : ''}</span></span>
    <span>Hora inicio: <span class="field-blank">${campo?.inicio_at ? `<span class="bold-u">${fmtHoraCorta(campo.inicio_at)}</span>` : ''}</span></span>
    <span>Hora final: <span class="field-blank">${campo?.fin_at ? `<span class="bold-u">${fmtHoraCorta(campo.fin_at)}</span>` : ''}</span></span>
  </div>

  <div class="tabla-wrap">
    <table class="productos">
      <thead>
        <tr>
          <th rowspan="2">#</th>
          <th rowspan="2">Nombre Comercial</th>
          <th rowspan="2">Fórmula o<br>Ingrediente Activo</th>
          <th rowspan="2">Justificación</th>
          <th>Cantidad<br>Has</th>
          <th>Cantidad x<br>${boomLabel}</th>
          <th>Fracción<br>Boon</th>
          <th>Aplicar</th>
          <th rowspan="2">Unidad<br>Medida</th>
          <th rowspan="2">Intervalo<br>Cosecha</th>
          <th rowspan="2">Período<br>Re-Ing</th>
        </tr>
      </thead>
      <tbody>
        ${productosRows}
        ${aguaRow}
        ${ultimaLinea}
      </tbody>
    </table>
  </div>

  <div class="footer">
    <div class="footer-top">
      <div class="bloque">
        <div class="bloque-label">Caldo sobrante</div>
        <div class="footer-checks">
          ${chk(campo?.caldo_sobrante === true)}Sí &nbsp;
          ${chk(campo?.caldo_sobrante === false && !!campo)}No
        </div>
      </div>
      <div class="bloque" style="flex:1">
        <div class="bloque-label">Aplicado en:</div>
        <div class="footer-checks">
          ${chk(campo?.cultivo)}Cultivo &nbsp;
          ${chk(campo?.semillero)}Semillero &nbsp;
          ${chk(campo?.area_barbecho)}Área de barbecho
        </div>
      </div>
      <div class="bloque" style="flex:1">
        <div class="bloque-label">Método de Aplicación</div>
        <div class="footer-checks">
          ${chk(campo?.metodo_aspersion)}Aspersión &nbsp;
          ${chk(campo?.metodo_inmersion)}Inmersión &nbsp;
          ${chk(campo?.metodo_nebulizacion)}Nebulización
        </div>
      </div>
    </div>

    <table class="calib-table">
      <thead>
        <tr>
          <th style="width:130px">Equipo Utilizado</th>
          <th colspan="3">Información Calibración</th>
          <th style="width:160px">Operador</th>
          <th style="width:160px">Firma</th>
        </tr>
        <tr>
          <th></th>
          <th>RPM</th><th>Presión</th><th>Marcha</th>
          <th></th><th></th>
        </tr>
      </thead>
      <tbody>
        <tr style="height:22px">
          <td class="left">${campo?.boom_descripcion ?? ''}</td>
          <td class="center">${campo?.rpm != null ? fmtNPrint(campo.rpm, 0) : ''}</td>
          <td class="center">${campo?.presion_bar != null ? fmtNPrint(campo.presion_bar, 2) : ''}</td>
          <td class="center">${campo?.marcha ?? ''}</td>
          <td class="left">${campo?.boomero_nombre ?? campo?.registrado_por ?? ''}</td>
          <td></td>
        </tr>
      </tbody>
    </table>

    <div style="margin-top:4px;font-size:8pt;">
      <strong>Condiciones climáticas en el momento de aplicación</strong>
      &nbsp;&nbsp;
      ${chk(campo?.clima_nublado)}Nublado &nbsp;
      ${chk(campo?.clima_lluvioso)}Lluvioso &nbsp;
      ${chk(campo?.clima_soleado)}Soleado &nbsp;
      ${chk(campo?.clima_ventoso)}Ventoso &nbsp;
      ${chk(campo?.clima_no_apto)}No Apto
    </div>
    ${campo?.inicio_at || campo?.fin_at ? `
    <div style="margin-top:6px;font-size:8pt;display:flex;gap:18px;flex-wrap:wrap;">
      ${campo?.inicio_at ? `<span><strong>Inicio:</strong> ${fmtDateTimeShort(campo.inicio_at)}${campo.inicio_lat != null && campo.inicio_lng != null ? ` &nbsp;<span style="font-family:monospace;color:#555">${Number(campo.inicio_lat).toFixed(6)}, ${Number(campo.inicio_lng).toFixed(6)}</span>` : ''}</span>` : ''}
      ${campo?.fin_at ? `<span><strong>Fin:</strong> ${fmtDateTimeShort(campo.fin_at)}${campo.fin_lat != null && campo.fin_lng != null ? ` &nbsp;<span style="font-family:monospace;color:#555">${Number(campo.fin_lat).toFixed(6)}, ${Number(campo.fin_lng).toFixed(6)}</span>` : ''}</span>` : ''}
    </div>
    ` : ''}
    <div style="margin-top:3px;font-size:8pt;"><strong>Observaciones:</strong> ${campo?.observaciones ? String(campo.observaciones).replace(/</g,'&lt;') : ''}</div>
    <div style="border-bottom:1px solid #bbb;margin-top:${campo?.observaciones ? '9' : '14'}px;"></div>
    <div style="border-bottom:1px solid #bbb;margin-top:14px;"></div>
    <div style="border-bottom:1px solid #bbb;margin-top:14px;"></div>
    <div style="border-bottom:1px solid #bbb;margin-top:14px;"></div>

    <div class="footer-firmas">
      <div class="firma-bloque"><div>Firma bodeguero</div></div>
      <div class="firma-bloque"><div>Firma Ingeniero</div></div>
    </div>

    <div class="footer-bottom">
      <span>Programa: Aplicación de Agroquimicos</span>
      <span>Programa de manejo fitosanitario autorizado por: Ing. Freddy Tobías Ramírez Espínoza Cod: 6480</span>
      <span>Boleta: rb03</span>
    </div>
  </div>

</div>
</body>
</html>`;
}

export async function imprimirBoletas(
  boletas: AppBoleta[],
  getProductos: (id: number) => Promise<AppBoletaProducto[]>,
  empresaNombre: string,
  empresaCedula: string,
  empresaLogo = '',
  getReporteCampo?: (id: number) => Promise<AppBoletaCampo | null>,
) {
  if (boletas.length === 0) return;

  // Al imprimir varias boletas, ordenarlas por ESTADIO (etapa) siguiendo la
  // secuencia agronómica del cultivo (1er ciclo → 2º ciclo); dentro del mismo
  // estadio, por número de lote. Se ordena ANTES de cargar productos para que
  // los índices de allProds/allCampo queden alineados con el orden de impresión.
  const ORDEN_ESTADIO: Record<string, number> = {
    'Preparación': 0,
    'Desarrollo PC': 1,
    'Post Forza PC': 2,
    'Semillero RC': 3,
    'Desarrollo RC': 4,
    'Post Forza RC': 5,
  };
  const rankEstadio = (b: AppBoleta) => {
    const e = (b.etapa_nombre ?? '').trim();
    return e in ORDEN_ESTADIO ? ORDEN_ESTADIO[e] : 99;
  };
  const loteKey = (b: AppBoleta) =>
    String(b.lote_num ?? '').padStart(6, '0');
  boletas = [...boletas].sort((a, b) =>
    rankEstadio(a) - rankEstadio(b) ||
    (a.etapa_nombre ?? '').localeCompare(b.etapa_nombre ?? '') ||
    loteKey(a).localeCompare(loteKey(b), undefined, { numeric: true })
  );

  const allProds = await Promise.all(boletas.map(b => getProductos(b.id)));
  const allCampo = getReporteCampo
    ? await Promise.all(boletas.map(b => getReporteCampo(b.id).catch(() => null)))
    : boletas.map(() => null);

  const cssMatch = buildBoletaHTML(boletas[0], allProds[0], empresaNombre, empresaCedula, '', empresaLogo, allCampo[0])
    .match(/<style>([\s\S]*?)<\/style>/);
  const css = cssMatch ? cssMatch[1] : '';

  const pagesHtml = boletas.map((b, i) => {
    const ubicacion = b.finca_nombre ?? '';
    const html = buildBoletaHTML(b, allProds[i], empresaNombre, empresaCedula, ubicacion, empresaLogo, allCampo[i]);
    const body = html.replace(/[\s\S]*<body>/, '').replace(/<\/body>[\s\S]*/, '');
    return `<div class="boleta-page">${body}</div>`;
  }).join('<div style="page-break-after:always"></div>');

  const fullHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Boleta</title><style>
    ${css}
    .boleta-page { margin-bottom: 0; }
  </style></head><body>${pagesHtml}</body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) { document.body.removeChild(iframe); return; }
  doc.open(); doc.write(fullHtml); doc.close();
  setTimeout(() => {
    if (iframe.contentDocument) iframe.contentDocument.title = 'Boleta';
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 600);
}
